import { useEffect, useState, type MouseEvent } from 'react';
import { Share2, X, Download, Copy, Link2, Check, AlertCircle } from 'lucide-react';
import { LoadingIndicator } from './LoadingIndicator';
import {
  renderBeforeAfterCard,
  canvasToPngBlob,
  buildShortShareUrl,
  type ShareCode,
  type BeforeAfterImageSource,
  type BeforeAfterLayout,
} from '../utils/beforeAfterCard';

export interface BeforeAfterSources {
  before: BeforeAfterImageSource | BeforeAfterImageSource[];
  after: BeforeAfterImageSource;
}

interface ShareBeforeAfterProps {
  // Lazy so tools only render their final output when the user actually shares
  getSources: () => BeforeAfterSources | Promise<BeforeAfterSources>;
  // Name as it appears in the site's Apps menu
  appName: string;
  shareCode: ShareCode;
  fileName?: string;
  variant?: 'icon' | 'button';
  // Icon variant only: 'sm' sits beside compact controls like the gallery card buttons
  size?: 'sm' | 'md';
  className?: string;
  // Called when the share button is clicked, e.g. to record that the image was shared
  onShareClick?: () => void;
}

// site-btn-primary takes colors, text color and radius from admin > Site Styles
export function ShareBeforeAfterButton({ variant = 'button', size = 'md', className = '', onShareClick, ...modalProps }: ShareBeforeAfterProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
    onShareClick?.();
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          onClick={handleOpen}
          className={`site-btn-primary ${size === 'sm' ? 'p-1 shadow-sm' : 'p-1.5 shadow-md'} transition-colors ${className}`}
          title="Share before & after"
        >
          <Share2 className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        </button>
      ) : (
        <button
          onClick={handleOpen}
          className={`site-btn-primary px-5 py-2.5 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 ${className}`}
        >
          <Share2 className="w-4 h-4" />
          Share Before &amp; After
        </button>
      )}
      {open && <ShareBeforeAfterModal {...modalProps} onClose={() => setOpen(false)} />}
    </>
  );
}

const LAYOUT_OPTIONS: { value: BeforeAfterLayout; label: string }[] = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
];

function ShareBeforeAfterModal({
  getSources,
  appName,
  shareCode,
  fileName = 'before-after',
  onClose,
}: Omit<ShareBeforeAfterProps, 'variant' | 'className'> & { onClose: () => void }) {
  const [layout, setLayout] = useState<BeforeAfterLayout>('landscape');
  const [sources, setSources] = useState<BeforeAfterSources | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'image' | 'link' | null>(null);

  const appLink = buildShortShareUrl(shareCode);
  const fullFileName = `${fileName}-${layout}.png`;
  const canCopyImage = typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
  const canNativeShare = !!blob && typeof navigator.canShare === 'function'
    && navigator.canShare({ files: [new File([blob], fullFileName, { type: 'image/png' })] });

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(getSources())
      .then((resolved) => { if (!cancelled) setSources(resolved); })
      .catch((err) => {
        console.error('Failed to prepare share images:', err);
        if (!cancelled) setError('Could not load the images for this card.');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sources) return;
    let cancelled = false;
    setBlob(null);
    setError(null);

    renderBeforeAfterCard({ ...sources, appName, layout })
      .then(canvasToPngBlob)
      .then((rendered) => { if (!cancelled) setBlob(rendered); })
      .catch((err) => {
        console.error('Failed to render before/after card:', err);
        if (!cancelled) setError('Could not create the share card. One of the images may not allow being copied.');
      });

    return () => { cancelled = true; };
  }, [sources, layout, appName]);

  useEffect(() => {
    if (!blob) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const flashCopied = (kind: 'image' | 'link') => {
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = fullFileName;
    link.click();
  };

  const handleCopyImage = async () => {
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flashCopied('image');
    } catch {
      setError('Your browser blocked copying the image. Use Download instead.');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(appLink);
      flashCopied('link');
    } catch {
      setError('Your browser blocked copying the link.');
    }
  };

  const handleNativeShare = async () => {
    if (!blob) return;
    try {
      await navigator.share({
        files: [new File([blob], fullFileName, { type: 'image/png' })],
        text: appLink,
      });
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        setError('Sharing failed. Use Download or Copy instead.');
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Share2 className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Before &amp; After</h2>
              <p className="text-sm text-gray-500">Send it to your team for sign-off, or to a fellow seller who&rsquo;d find it useful.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          <div role="radiogroup" aria-label="Layout" className="flex items-center justify-center gap-5 text-sm">
            {LAYOUT_OPTIONS.map((option) => (
              <label key={option.value} className="inline-flex items-center gap-2 cursor-pointer text-gray-900">
                <input
                  type="radio"
                  name="share-card-layout"
                  value={option.value}
                  checked={layout === option.value}
                  onChange={() => setLayout(option.value)}
                  className="w-4 h-4 accent-primary-600 cursor-pointer"
                />
                {option.label}
              </label>
            ))}
          </div>

          <div className={`mx-auto rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden ${
            layout === 'landscape' ? 'aspect-[16/9] w-full' : 'aspect-[4/5] w-full max-w-sm'
          }`}>
            {previewUrl ? (
              <img src={previewUrl} alt="Before and after share card" className="w-full h-full object-contain" />
            ) : error ? (
              <div className="flex items-center gap-2 text-sm text-secondary-700 px-6 text-center">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            ) : (
              <LoadingIndicator size="sm" fullPage={false} />
            )}
          </div>

          {error && previewUrl && (
            <p className="text-sm text-secondary-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {canNativeShare && (
              <button
                onClick={handleNativeShare}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            )}
            <button
              onClick={handleDownload}
              disabled={!blob}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                canNativeShare ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            {canCopyImage && (
              <button
                onClick={handleCopyImage}
                disabled={!blob}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copied === 'image' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied === 'image' ? 'Copied' : 'Copy Image'}
              </button>
            )}
            <button
              onClick={handleCopyLink}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              {copied === 'link' ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
              {copied === 'link' ? 'Copied' : 'Copy App Link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
